import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { taskService } from '../services/taskService';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { Task as TaskType } from '../types';

interface TaskWithProject extends TaskType {
  projectName: string;
  projectKey: string;
}

const TasksPage: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  
  const [tasks, setTasks] = useState<TaskWithProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProject, setFilterProject] = useState<string>('all');
  const [filterAssignee, setFilterAssignee] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      setLoading(true);
      // For now, we'll need to implement a global tasks endpoint
      // This is a placeholder that will need backend support
      try {
        const response = await taskService.getAllTasks() as any;
        setTasks(response || []);
      } catch (apiError: any) {
        console.error('API call failed:', apiError);
        // Silently fail for now since endpoint might not exist yet
        setTasks([]);
      }
    } catch (error) {
      console.error('Failed to load tasks:', error);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityLabel = (priority: string | number): string => {
    const map: Record<string, string> = { '1': 'Low', '2': 'Medium', '3': 'High', '4': 'Urgent',
      'low': 'Low', 'medium': 'Medium', 'high': 'High', 'urgent': 'Urgent' };
    return map[String(priority).toLowerCase()] || String(priority);
  };

  const getPriorityColor = (priority: string | number) => {
    const p = String(priority).toLowerCase();
    switch (p) {
      case '4': case 'critical': case 'urgent': return 'error';
      case '3': case 'high': return 'warning';
      case '2': case 'medium': return 'info';
      case '1': case 'low': return 'success';
      default: return 'default';
    }
  };

  const filteredTasks = tasks.filter((task) => {
    try {
      const matchesSearch = 
        task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.projectName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (task.description || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesProject = filterProject === 'all' || task.projectId === filterProject;
      const matchesAssignee = 
        filterAssignee === 'all' || 
        (filterAssignee === 'me' && task.assignee?.id === currentUser?.id) ||
        task.assignee?.id === filterAssignee;
      const matchesStatus = filterStatus === 'all' || task.statusId === filterStatus;
      const matchesPriority = filterPriority === 'all' || task.priority === filterPriority;

      return matchesSearch && matchesProject && matchesAssignee && matchesStatus && matchesPriority;
    } catch (err) {
      console.error('Error filtering task:', task, err);
      return false;
    }
  });

  const uniqueProjects = Array.from(new Map(tasks.map(t => [t.projectId, { id: t.projectId, name: t.projectName }])).values());
  const uniqueStatuses = Array.from(new Set(tasks.map(t => t.statusId).filter(Boolean)));
  const uniquePriorities = Array.from(new Set(tasks.map(t => t.priority).filter(Boolean)));

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            All Tasks
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredTasks.length} {filteredTasks.length === 1 ? 'task' : 'tasks'}
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Project</InputLabel>
              <Select
                value={filterProject}
                label="Project"
                onChange={(e) => setFilterProject(e.target.value)}
              >
                <MenuItem value="all">All Projects</MenuItem>
                {uniqueProjects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Assignee</InputLabel>
              <Select
                value={filterAssignee}
                label="Assignee"
                onChange={(e) => setFilterAssignee(e.target.value)}
              >
                <MenuItem value="all">All Users</MenuItem>
                <MenuItem value="me">My Tasks</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                {uniqueStatuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={filterPriority}
                label="Priority"
                onChange={(e) => setFilterPriority(e.target.value)}
              >
                <MenuItem value="all">All Priorities</MenuItem>
                {uniquePriorities.map((priority) => (
                  <MenuItem key={priority} value={priority}>
                    {priority}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Tasks Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Task</TableCell>
              <TableCell>Project</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Assignee</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTasks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box sx={{ py: 4 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No tasks found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm || filterProject !== 'all' || filterAssignee !== 'all' || filterStatus !== 'all' || filterPriority !== 'all'
                        ? 'Try adjusting your filters'
                        : 'Create your first task to get started'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filteredTasks.map((task) => (
                <TableRow 
                  key={task.id} 
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/tasks/${task.id}`)}
                >
                  <TableCell>
                    <Typography variant="body1" fontWeight="500">
                      {task.title}
                    </Typography>
                    {task.description && (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {task.description.substring(0, 50)}
                        {task.description.length > 50 ? '...' : ''}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={(task as any).projectKey || (task as any).projectName || 'N/A'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={(task as any).statusName || 'Unknown'}
                      size="small"
                      variant="outlined"
                      sx={(task as any).statusColor ? { borderColor: (task as any).statusColor, color: (task as any).statusColor } : {}}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getPriorityLabel(task.priority)}
                      size="small"
                      color={getPriorityColor(task.priority) as any}
                    />
                  </TableCell>
                  <TableCell>
                    {task.assignee ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28 }}>
                          {task.assignee.firstName[0]}
                        </Avatar>
                        <Typography variant="body2">
                          {task.assignee.firstName} {task.assignee.lastName}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Unassigned
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {task.dueDate ? (
                      <Typography variant="body2">
                        {new Date(task.dueDate).toLocaleDateString()}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No due date
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/tasks/${task.id}`);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default TasksPage;
